import { useEffect, useRef, useState, useCallback } from 'react';
import { Client } from '@stomp/stompjs';
import { WS_BASE_URL } from '../config';

/**
 * Hook para gestionar WebSocket de expediente con JWT
 * @param {Object} params
 * @param {number|string} params.expedienteId
 * @param {number|string} params.userId
 * @param {function} params.onLockChange
 * @param {function} params.onTakeoverRequest
 * @param {function} params.onTakeoverResponse
 */
export function useExpedienteWebSocket({ expedienteId, userId, onLockChange, onTakeoverRequest, onTakeoverResponse }) {
  const clientRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const lastLockRequestRef = useRef(0);
  const lastUnlockRequestRef = useRef(0);
  const lastTakeoverRequestRef = useRef(0);
  const isMountedRef = useRef(false); // Flag para saber si el componente está montado
  const DEBOUNCE_TIME = 1000; // 1 segundo entre peticiones

  // Función para desbloquear expediente usando useCallback para evitar re-renders
  const unlockExpediente = useCallback(() => {
    if (!clientRef.current?.connected) {
      console.error('WebSocket no está conectado');
      return false;
    }

    // Debounce: evitar envíos múltiples
    const now = Date.now();
    if (now - lastUnlockRequestRef.current < DEBOUNCE_TIME) {
      console.log('Debounce: ignorando solicitud de desbloqueo duplicada');
      return false;
    }
    lastUnlockRequestRef.current = now;

    try {
      clientRef.current.publish({
        destination: '/app/expediente/unlock',
        body: JSON.stringify({ 
          expedienteId: parseInt(expedienteId), 
          userId: parseInt(userId) 
        })
      });
      console.log('Mensaje de desbloqueo enviado:', { expedienteId, userId });
      return true;
    } catch (error) {
      console.error('Error enviando mensaje de desbloqueo:', error);
      return false;
    }
  }, [expedienteId, userId]);

  useEffect(() => {
    if (!expedienteId || !userId) {
      console.warn('ExpedienteId o UserId no proporcionados para WebSocket');
      return;
    }

    // Usar SockJS en vez de WebSocket puro
    const { Client } = require('@stomp/stompjs');
    const SockJS = require('sockjs-client');
    const socket = new SockJS(WS_BASE_URL);
    const client = new Client({
      webSocketFactory: () => socket,
      debug: (str) => {
        console.log('[WebSocket Debug]:', str);
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      // connectHeaders eliminado porque ya no se usa JWT
    });

    client.onConnect = (frame) => {
      console.log('Conectado al WebSocket:', frame);
      setIsConnected(true);
      setConnectionError(null);

      // Suscribirse a los cambios de bloqueo del expediente
      client.subscribe('/topic/expediente-lock', (message) => {
        try {
          const data = JSON.parse(message.body);
          console.log('[WebSocket] Mensaje de bloqueo recibido:', data);
          console.log('[WebSocket] expedienteId del mensaje:', data.expedienteId);
          console.log('[WebSocket] Mi expedienteId:', expedienteId);
          
          // 🔥 FILTRO CRÍTICO: Solo procesar si es MI expediente
          if (data.expedienteId && parseInt(data.expedienteId) === parseInt(expedienteId)) {
            console.log('[WebSocket] ✅ Evento de bloqueo es para MI expediente - procesando');
            if (onLockChange) {
              onLockChange(data);
            }
          } else {
            console.log('[WebSocket] ❌ Evento de bloqueo es para OTRO expediente - ignorando');
            console.log('[WebSocket] - expedienteId del mensaje:', data.expedienteId);
            console.log('[WebSocket] - Mi expedienteId:', expedienteId);
          }
        } catch (error) {
          console.error('Error parsing lock message:', error);
        }
      });

      // Suscribirse a las notificaciones de takeover (topic general, filtrado por targetUserId)
      client.subscribe('/topic/expediente-takeover', (message) => {
        try {
          const data = JSON.parse(message.body);
          console.log('[WebSocket] ===== TAKEOVER NOTIFICATION =====');
          console.log('[WebSocket] Raw message:', message);
          console.log('[WebSocket] Parsed data:', data);
          console.log('[WebSocket] expedienteId del mensaje:', data.expedienteId);
          console.log('[WebSocket] Mi expedienteId:', expedienteId);
          console.log('[WebSocket] targetUserId:', data.targetUserId);
          console.log('[WebSocket] Mi userId:', userId, 'tipo:', typeof userId);
          console.log('[WebSocket] targetUserId parsed:', parseInt(data.targetUserId));
          
          // 🔥 DOBLE FILTRO: Verificar expediente Y usuario
          const isMyExpediente = data.expedienteId && parseInt(data.expedienteId) === parseInt(expedienteId);
          const isForMe = data.targetUserId && parseInt(data.targetUserId) === parseInt(userId);
          
          console.log('[WebSocket] ¿Es mi expediente?', isMyExpediente);
          console.log('[WebSocket] ¿Es para mí?', isForMe);
          
          if (isMyExpediente && isForMe) {
            console.log('[WebSocket] ✅ Takeover dirigido a mí EN MI expediente - llamando onTakeoverRequest');
            if (onTakeoverRequest) {
              onTakeoverRequest(data);
            }
          } else {
            console.log('[WebSocket] ❌ Takeover NO dirigido a mí o es de otro expediente');
            console.log('[WebSocket] - expedienteId:', data.expedienteId, 'vs mi expedienteId:', expedienteId);
            console.log('[WebSocket] - targetUserId:', data.targetUserId, 'vs mi userId:', userId);
          }
        } catch (error) {
          console.error('[WebSocket] Error parsing takeover notification:', error);
        }
      });

      // 🆕 Suscribirse a las RESPUESTAS de takeover (para el solicitante)
      client.subscribe('/topic/expediente-takeover-response', (message) => {
        try {
          const data = JSON.parse(message.body);
          console.log('[WebSocket] ===== TAKEOVER RESPONSE =====');
          console.log('[WebSocket] Response data:', data);
          console.log('[WebSocket] expedienteId del mensaje:', data.expedienteId);
          console.log('[WebSocket] Mi expedienteId:', expedienteId);
          console.log('[WebSocket] requesterId:', data.requesterId);
          console.log('[WebSocket] Mi userId:', userId);
          console.log('[WebSocket] accepted:', data.accepted);
          
          // Filtrar: solo procesar si es MI expediente Y yo soy el solicitante
          const isMyExpediente = data.expedienteId && parseInt(data.expedienteId) === parseInt(expedienteId);
          const isMyRequest = data.requesterId && parseInt(data.requesterId) === parseInt(userId);
          
          console.log('[WebSocket] ¿Es mi expediente?', isMyExpediente);
          console.log('[WebSocket] ¿Soy yo el solicitante?', isMyRequest);
          
          if (isMyExpediente && isMyRequest) {
            console.log('[WebSocket] ✅ Respuesta de takeover para MI solicitud');
            if (onTakeoverResponse) {
              onTakeoverResponse(data);
            }
          } else {
            console.log('[WebSocket] ❌ Respuesta NO es para mi solicitud');
          }
        } catch (error) {
          console.error('[WebSocket] Error parsing takeover response:', error);
        }
      });

      console.log('[WebSocket] Suscripciones completadas - listo para recibir mensajes');
    };

    client.onStompError = (frame) => {
      console.error('Error en WebSocket STOMP:', frame.headers['message']);
      console.error('Detalles adicionales:', frame.body);
      setIsConnected(false);
    };

    client.onWebSocketError = (error) => {
      console.error('Error en WebSocket:', error);
      setConnectionError('Error de conexión WebSocket');
      setIsConnected(false);
    };

    client.onDisconnect = () => {
      console.log('Desconectado del WebSocket');
      setIsConnected(false);
    };

    // Función para desbloquear el expediente antes de salir
    const handleBeforeUnload = (e) => {
      console.log('[WebSocket] beforeunload - desbloqueando expediente');
      if (clientRef.current?.connected) {
        // Enviar unlock de forma síncrona antes de que se cierre la página
        try {
          clientRef.current.publish({
            destination: '/app/expediente/unlock',
            body: JSON.stringify({ 
              expedienteId: parseInt(expedienteId), 
              userId: parseInt(userId) 
            })
          });
          console.log('[WebSocket] Unlock enviado en beforeunload');
        } catch (error) {
          console.error('[WebSocket] Error en beforeunload unlock:', error);
        }
      }
    };

    // NO usar visibilitychange porque se dispara al abrir DevTools, cambiar de ventana, etc.
    // Esto causaba que el lock se liberara prematuramente

    try {
      client.activate();
      clientRef.current = client;
      isMountedRef.current = true; // Marcar como montado

      // Agregar listeners para beforeunload y pagehide (cierre real de página)
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('pagehide', handleBeforeUnload);
    } catch (error) {
      console.error('Error activando cliente WebSocket:', error);
      setConnectionError('Error al conectar con WebSocket');
    }

    // Cleanup al desmontar el componente
    return () => {
      console.log('[WebSocket] Ejecutando cleanup - isMountedRef:', isMountedRef.current);
      
      // Remover listeners
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
      
      // SOLO desconectar el WebSocket, NO hacer unlock
      // El unlock solo debe ocurrir en beforeunload (cierre real) o cuando el usuario sale manualmente
      if (clientRef.current) {
        try {
          console.log('[WebSocket] Desactivando cliente WebSocket sin hacer unlock');
          clientRef.current.deactivate();
          isMountedRef.current = false; // Marcar como desmontado
        } catch (error) {
          console.error('Error durante cleanup de WebSocket:', error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expedienteId, userId]);

  // Función para bloquear expediente
  const lockExpediente = useCallback(() => {
    if (!clientRef.current?.connected) {
      console.error('WebSocket no está conectado');
      return false;
    }

    // Debounce: evitar envíos múltiples
    const now = Date.now();
    if (now - lastLockRequestRef.current < DEBOUNCE_TIME) {
      console.log('Debounce: ignorando solicitud de bloqueo duplicada');
      return false;
    }
    lastLockRequestRef.current = now;

    try {
      clientRef.current.publish({
        destination: '/app/expediente/lock',
        body: JSON.stringify({ 
          expedienteId: parseInt(expedienteId), 
          userId: parseInt(userId) 
        })
      });
      console.log('Mensaje de bloqueo enviado:', { expedienteId, userId });
      return true;
    } catch (error) {
      console.error('Error enviando mensaje de bloqueo:', error);
      return false;
    }
  }, [expedienteId, userId]);

  // Función para solicitar takeover (tomar control del expediente)
  const requestTakeover = useCallback(() => {
    if (!clientRef.current?.connected) {
      console.error('WebSocket no está conectado');
      return false;
    }

    // Debounce: evitar envíos múltiples
    const now = Date.now();
    if (now - lastTakeoverRequestRef.current < DEBOUNCE_TIME) {
      console.log('Debounce: ignorando solicitud de takeover duplicada');
      return false;
    }
    lastTakeoverRequestRef.current = now;

    try {
      clientRef.current.publish({
        destination: '/app/expediente/requestTakeover',
        body: JSON.stringify({ 
          expedienteId: parseInt(expedienteId), 
          userId: parseInt(userId)
        })
      });
      console.log('Solicitud de takeover enviada:', { expedienteId, userId });
      return true;
    } catch (error) {
      console.error('Error enviando solicitud de takeover:', error);
      return false;
    }
  }, [expedienteId, userId]);

  // Función para responder a una solicitud de takeover
  const respondTakeover = useCallback((newOwnerId, accepted) => {
    if (!clientRef.current?.connected) {
      console.error('WebSocket no está conectado');
      return false;
    }

    try {
      clientRef.current.publish({
        destination: '/app/expediente/resolveTakeover',
        body: JSON.stringify({ 
          expedienteId: parseInt(expedienteId), 
          currentOwner: parseInt(userId),
          newOwner: parseInt(newOwnerId),
          accepted: Boolean(accepted)
        })
      });
      console.log('Respuesta de takeover enviada:', { expedienteId, currentOwner: userId, newOwner: newOwnerId, accepted });
      return true;
    } catch (error) {
      console.error('Error enviando respuesta de takeover:', error);
      return false;
    }
  }, [expedienteId, userId]);

  return { 
    lockExpediente, 
    unlockExpediente, 
    requestTakeover,
    respondTakeover,
    isConnected,
    connectionError
  };
}